import {getTestProps} from '@benchling/js-test-utils';
import {useMantineTheme} from '@mantine/core';
import React, {useState} from 'react';
import {BaseEdge, getBezierPath} from 'reactflow';

import {assert} from 'Asserts';
import {assertNever} from 'util/Types';

import {DEFAULT_EDGE_OPTIONS, BenchlingEdgeProps, EdgeType, BezierEdgeRendererData} from './edgeOptions';
import {getEdgePathStyle} from './edgeUtils';
import ElkSplineEdgeRenderer from './ElkSplineEdgeRenderer';

const DEFAULT_BASE_EDGE_OPTIONS: BezierEdgeRendererData = {
  ...DEFAULT_EDGE_OPTIONS,
  type: EdgeType.BEZIER,
};

/**
 * This is a simple bezier edge renderer which can be used for many simple edge types.
 * It renders a bezier curve between two nodes with a default style.
 */
export default function BaseEdgeRenderer({
  data = DEFAULT_BASE_EDGE_OPTIONS,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  markerEnd,
  selected,
  label,
  id,
  source,
  target,
}: BenchlingEdgeProps): JSX.Element {
  const theme = useMantineTheme();
  const [hovered, setHovered] = useState(false);

  let edgeElement: JSX.Element;

  if (data.type === EdgeType.BEZIER) {
    const [edgePath, labelX, labelY] = getBezierPath({
      sourceX,
      sourceY,
      sourcePosition,
      targetX,
      targetY,
      targetPosition,
    });

    edgeElement = (
      <BaseEdge
        path={edgePath}
        markerEnd={markerEnd}
        style={getEdgePathStyle(theme, data, selected ?? false, hovered)}
        label={label}
        labelX={labelX}
        labelY={labelY}
      />
    );
  } else if (data.type === EdgeType.ELK_SPLINE) {
    edgeElement = (
      <ElkSplineEdgeRenderer
        data={data}
        sourceX={sourceX}
        sourceY={sourceY}
        targetX={targetX}
        targetY={targetY}
        sourcePosition={sourcePosition}
        targetPosition={targetPosition}
        markerEnd={markerEnd}
        selected={selected}
        label={label}
        id={id}
        source={source}
        target={target}
        hovered={hovered}
      />
    );
  } else {
    assert(false, `Unknown edge type: ${assertNever(data)}`);
  }

  return (
    <g
      {...getTestProps({component: 'BaseEdgeRenderer', element: 'root', key: id})}
      onMouseEnter={
        data.hoverColor
          ? () => {
              setHovered(true);
            }
          : undefined
      }
      onMouseLeave={
        data.hoverColor
          ? () => {
              setHovered(false);
            }
          : undefined
      }
    >
      {edgeElement}
    </g>
  );
}
